import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { validateRut } from '../utils/rutValidator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  UploadSimple, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Download,
  CircleNotch,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function BulkUploadDialog({ 
  open, 
  onOpenChange, 
  title,
  templateHeaders,
  templateExample,
  fieldNames,
  onUpload,
  entityName = 'registros',
  rutRequired = false
}) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const stripAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const excelSerialToDate = (serial) => {
    if (typeof serial !== 'number' || serial < 1) return String(serial);
    const utcDays = Math.floor(serial - 25569);
    const d = new Date(utcDays * 86400000);
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseExcel = (buffer) => {
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });
    // Build mapping from display header (lowercase, no accents) -> field name
    const headerMap = {};
    if (fieldNames && templateHeaders) {
      templateHeaders.forEach((h, i) => {
        if (fieldNames[i]) headerMap[stripAccents(h.trim().toLowerCase())] = fieldNames[i];
      });
    }
    // Fields that should be treated as dates
    const dateFields = fieldNames ? fieldNames.filter(f => f.includes('fecha') || f.includes('date')) : [];
    const phoneFields = fieldNames ? fieldNames.filter(f => f.includes('phone') || f.includes('telefono') || f.includes('fono')) : [];
    return jsonData.map(row => {
      const normalized = {};
      Object.keys(row).forEach(key => {
        const lowerKey = stripAccents(key.trim().toLowerCase());
        const mappedKey = headerMap[lowerKey] || lowerKey;
        let value = row[key];
        if (dateFields.includes(mappedKey) && typeof value === 'number') {
          value = excelSerialToDate(value);
        }
        if (phoneFields.includes(mappedKey) && typeof value === 'number') {
          value = String(Math.round(value));
        }
        normalized[mappedKey] = String(value).trim();
      });
      return normalized;
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    const validExts = ['.xlsx', '.xls'];
    const hasValidExt = validExts.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
    if (!hasValidExt) {
      toast.error('Por favor selecciona un archivo Excel (.xlsx o .xls)');
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = parseExcel(event.target.result);
      setParsedData(data);
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleUpload = async () => {
    if (parsedData.length === 0) {
      toast.error('No hay datos para cargar');
      return;
    }
    
    // Validate RUTs if rut column exists (obligatorio + dígito verificador)
    const hasRut = fieldNames ? fieldNames.includes('rut') : templateHeaders.includes('rut');
    if (hasRut) {
      const rutErrors = [];
      parsedData.forEach((row, idx) => {
        const rutVal = (row.rut || '').trim();
        if (!rutVal) {
          if (rutRequired) rutErrors.push(`Fila ${idx + 1}: RUT vacío (obligatorio)`);
          return;
        }
        const check = validateRut(rutVal);
        if (!check.valid) {
          rutErrors.push(`Fila ${idx + 1}: ${check.error} (${rutVal})`);
        }
      });
      if (rutErrors.length > 0) {
        toast.error(`Filas con RUT inválido (corrígelas para continuar):\n${rutErrors.slice(0, 4).join('\n')}${rutErrors.length > 4 ? `\n...y ${rutErrors.length - 4} más` : ''}`);
        return;
      }
    }
    
    setUploading(true);
    try {
      const response = await onUpload(parsedData);
      setResult(response);
      if (response.created > 0) {
        toast.success(`${response.created} ${entityName} creados exitosamente`);
      }
      if (response.skipped > 0) {
        toast.warning(`${response.skipped} ${entityName} omitidos`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar datos');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const wsData = [templateHeaders, templateExample];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, `plantilla_${entityName}.xlsx`);
  };

  const resetDialog = () => {
    setFile(null);
    setParsedData([]);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = (open) => {
    if (!open) {
      resetDialog();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-[95vw] w-fit max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
            <UploadSimple size={24} className="text-[#2BBAA4]" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Instructions */}
          <div className="p-4 rounded-lg bg-[#2BBAA4]/5 border border-[#2BBAA4]/20">
            <h4 className="font-medium text-[#2B4B8C] mb-2">Instrucciones:</h4>
            <ul className="text-sm text-[#2B4B8C]/70 space-y-1 list-disc list-inside">
              <li>Descarga la plantilla Excel para ver el formato requerido</li>
              <li>Completa el archivo con los datos a cargar</li>
              <li>Sube el archivo Excel y revisa la vista previa</li>
              <li>Haz clic en "Cargar Datos" para procesar</li>
            </ul>
          </div>

          {/* Download Template */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/10">
            <div>
              <p className="font-medium text-[#2B4B8C]">Plantilla Excel</p>
              <p className="text-sm text-[#2B4B8C]/60">Descarga el formato requerido</p>
            </div>
            <Button 
              variant="outline" 
              onClick={downloadTemplate}
              className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#2B4B8C]/5"
            >
              <Download size={18} className="mr-2" />
              Descargar Plantilla
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label className="text-[#2B4B8C]">Archivo Excel</Label>
            <div 
              className="border-2 border-dashed border-[#2B4B8C]/20 rounded-lg p-8 text-center hover:border-[#2BBAA4]/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText size={32} className="text-[#2BBAA4]" />
                  <div className="text-left">
                    <p className="font-medium text-[#2B4B8C]">{file.name}</p>
                    <p className="text-sm text-[#2B4B8C]/60">{parsedData.length} filas detectadas</p>
                  </div>
                </div>
              ) : (
                <div>
                  <UploadSimple size={32} className="mx-auto text-[#2B4B8C]/40 mb-2" />
                  <p className="text-[#2B4B8C]/60">Haz clic para seleccionar un archivo Excel</p>
                </div>
              )}
            </div>
          </div>

          {/* Preview Table */}
          {parsedData.length > 0 && (
            <div className="space-y-2">
              <Label className="text-[#2B4B8C]">Vista Previa ({Math.min(5, parsedData.length)} de {parsedData.length} filas)</Label>
              <div className="border border-[#2B4B8C]/10 rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#f8fafc]">
                      {Object.keys(parsedData[0]).map((header, i) => {
                        const displayName = fieldNames ? (templateHeaders[fieldNames.indexOf(header)] || header) : (stripAccents ? header : header);
                        return (
                          <TableHead key={header} className="text-[#2B4B8C]/70 text-[10px] uppercase px-2 py-1 whitespace-nowrap">
                            {displayName}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedData.slice(0, 5).map((row, idx) => (
                      <TableRow key={idx} className="border-[#2B4B8C]/5">
                        {Object.values(row).map((value, vidx) => (
                          <TableCell key={vidx} className="text-[#2B4B8C] text-xs px-2 py-1 whitespace-nowrap">
                            {value || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-lg border ${
              result.created > 0 && result.skipped === 0 
                ? 'bg-green-50 border-green-200' 
                : result.created > 0 
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                {result.created > 0 ? (
                  <CheckCircle size={24} className="text-green-600" weight="fill" />
                ) : (
                  <Warning size={24} className="text-yellow-600" weight="fill" />
                )}
                <div>
                  <p className="font-medium text-[#2B4B8C]">Resultado de la carga</p>
                  <p className="text-sm text-[#2B4B8C]/70">
                    {result.created} creados, {result.skipped} omitidos
                  </p>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-3 p-3 rounded bg-white/50">
                  <p className="text-sm font-medium text-[#2B4B8C] mb-2">Errores:</p>
                  <ul className="text-xs text-red-600 space-y-1">
                    {result.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-[#2B4B8C]/10">
            <Button 
              variant="outline" 
              onClick={() => handleClose(false)}
              className="flex-1 border-[#2B4B8C]/20 text-[#2B4B8C]"
            >
              {result ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!result && (
              <Button 
                onClick={handleUpload}
                disabled={parsedData.length === 0 || uploading}
                className="flex-1 bg-[#2BBAA4] text-white hover:bg-[#239986]"
              >
                {uploading ? (
                  <>
                    <CircleNotch className="animate-spin mr-2" size={18} />
                    Cargando...
                  </>
                ) : (
                  <>
                    <UploadSimple className="mr-2" size={18} />
                    Cargar Datos
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
